/**
 * /policies/leave/year-calendar — Session 3c PR 5.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { LeaveYearPeriodItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LeaveYearCalendarPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-leave-year-calendar", le],
    queryFn: () => session3cApi.listLeaveYearPeriods(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<LeaveYearPeriodItem>[] = [
    {
      headerKey: "policies.leave_year.col.type",
      label: "Period type",
      render: (r) => <span className="text-xs text-text-secondary">{r.period_type}</span>,
    },
    {
      headerKey: "policies.leave_year.col.range",
      label: "Date range",
      render: (r) => (
        <span dir="ltr" className="font-mono text-xs">{r.start_date} → {r.end_date}</span>
      ),
    },
    {
      headerKey: "policies.leave_year.col.active_from",
      label: "Active from",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.active_from}</span>,
    },
    {
      headerKey: "policies.leave_year.col.notes",
      label: "Notes",
      render: (r) => <span className="text-xs text-text-tertiary">{r.notes ?? "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<LeaveYearPeriodItem>
      title={t("policies.leave_year.title", "Leave Year Calendar")}
      subtitle={t("policies.leave_year.subtitle", "Per-LE leave year setting — Jan-Dec calendar · Apr-Mar fiscal · Hijri.")}
      agent="tashghil"
      domain="leave_year"
      newButtonLabel={t("policies.leave_year.cta.new", "New Year Period")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.leave_year.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.leave_year.suggestion.clean_body", "Current period defines how leave balances accrue + when carry-forward cycles trigger."),
          primaryActionLabel: t("policies.leave_year.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "leave-year-calendar-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.leave_year.title", "Leave Year Calendar"),
        window_label: t("policies.leave_year.window", "current + future periods"),
      }}
    />
  );
}
